import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { bgvApi } from '../../api/bgvApi';
import {
  BgvCaseDto,
  BgvCaseDetailDto,
  BgvDashboardDto,
  BgvVendorDto,
  BgvVerificationTypeDto,
  BgvCaseStatus,
  BgvFinalDecision,
  CreateBgvCase,
  CreateBgvVendor,
  UpdateBgvVendor,
  CreateBgvVerificationType,
  CreateBgvDiscrepancy,
  BgvTaskStatus,
  BgvDocumentStatus,
} from '../../types/bgvTypes';

interface BgvState {
  dashboard: BgvDashboardDto | null;
  cases: BgvCaseDto[];
  currentCase: BgvCaseDetailDto | null;
  vendors: BgvVendorDto[];
  verificationTypes: BgvVerificationTypeDto[];
  loading: boolean;
  error: string | null;
}

const initialState: BgvState = {
  dashboard: null,
  cases: [],
  currentCase: null,
  vendors: [],
  verificationTypes: [],
  loading: false,
  error: null,
};

export const fetchBgvDashboard = createAsyncThunk(
  'bgv/fetchDashboard',
  async (params: { fromDate?: string; toDate?: string } | undefined, { rejectWithValue }) => {
    try {
      return await bgvApi.getDashboard(params?.fromDate, params?.toDate);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch BGV dashboard');
    }
  }
);

export const fetchBgvCases = createAsyncThunk(
  'bgv/fetchCases',
  async (filters: {
    status?: BgvCaseStatus;
    finalDecision?: BgvFinalDecision;
    employeeId?: string;
    searchTerm?: string;
  } | undefined, { rejectWithValue }) => {
    try {
      return await bgvApi.getCases(filters);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch BGV cases');
    }
  }
);

export const fetchBgvCaseById = createAsyncThunk(
  'bgv/fetchCaseById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await bgvApi.getCaseById(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch case details');
    }
  }
);

export const createBgvCase = createAsyncThunk(
  'bgv/createCase',
  async (data: CreateBgvCase, { rejectWithValue }) => {
    try {
      return await bgvApi.createCase(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create case');
    }
  }
);

export const fetchBgvVendors = createAsyncThunk(
  'bgv/fetchVendors',
  async (params: { isActive?: boolean; searchTerm?: string } | undefined, { rejectWithValue }) => {
    try {
      return await bgvApi.getVendors(params?.isActive, params?.searchTerm);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vendors');
    }
  }
);

export const createBgvVendor = createAsyncThunk(
  'bgv/createVendor',
  async (data: CreateBgvVendor, { rejectWithValue }) => {
    try {
      return await bgvApi.createVendor(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create vendor');
    }
  }
);

export const updateBgvVendor = createAsyncThunk(
  'bgv/updateVendor',
  async ({ id, data }: { id: string; data: UpdateBgvVendor }, { rejectWithValue }) => {
    try {
      await bgvApi.updateVendor(id, data);
      return { id, data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update vendor');
    }
  }
);

export const fetchBgvVerificationTypes = createAsyncThunk(
  'bgv/fetchVerificationTypes',
  async (isActive: boolean | undefined, { rejectWithValue }) => {
    try {
      return await bgvApi.getVerificationTypes(isActive);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch verification types');
    }
  }
);

export const createBgvVerificationType = createAsyncThunk(
  'bgv/createVerificationType',
  async (data: CreateBgvVerificationType, { rejectWithValue }) => {
    try {
      return await bgvApi.createVerificationType(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create verification type');
    }
  }
);

export const updateBgvTaskStatus = createAsyncThunk(
  'bgv/updateTaskStatus',
  async ({ taskId, status, remarks }: { taskId: string; status: BgvTaskStatus; remarks?: string }, { rejectWithValue }) => {
    try {
      await bgvApi.updateTaskStatus(taskId, status, remarks);
      return { taskId, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update task status');
    }
  }
);

export const updateBgvCaseStatus = createAsyncThunk(
  'bgv/updateCaseStatus',
  async ({ id, status, remarks }: { id: string; status: BgvCaseStatus; remarks?: string }, { rejectWithValue }) => {
    try {
      await bgvApi.updateCaseStatus(id, status, remarks);
      return { id, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update case status');
    }
  }
);

export const updateBgvFinalDecision = createAsyncThunk(
  'bgv/updateFinalDecision',
  async ({ id, finalDecision, remarks }: { id: string; finalDecision: BgvFinalDecision; remarks?: string }, { rejectWithValue }) => {
    try {
      await bgvApi.updateFinalDecision(id, finalDecision, remarks);
      return { id, finalDecision };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update final decision');
    }
  }
);

export const uploadBgvDocument = createAsyncThunk(
  'bgv/uploadDocument',
  async ({ taskId, file }: { taskId: string; file: File }, { rejectWithValue }) => {
    try {
      return await bgvApi.uploadDocument(taskId, file);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload document');
    }
  }
);

export const updateBgvDocumentStatus = createAsyncThunk(
  'bgv/updateDocumentStatus',
  async ({ documentId, status, reviewRemarks }: { documentId: string; status: BgvDocumentStatus; reviewRemarks?: string }, { rejectWithValue }) => {
    try {
      await bgvApi.updateDocumentStatus(documentId, status, reviewRemarks);
      return { documentId, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update document status');
    }
  }
);

export const createBgvDiscrepancy = createAsyncThunk(
  'bgv/createDiscrepancy',
  async (data: CreateBgvDiscrepancy, { rejectWithValue }) => {
    try {
      return await bgvApi.createDiscrepancy(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create discrepancy');
    }
  }
);

const bgvSlice = createSlice({
  name: 'bgv',
  initialState,
  reducers: {
    clearCurrentCase: (state) => {
      state.currentCase = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchBgvDashboard.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBgvDashboard.fulfilled, (state, action) => { state.loading = false; state.dashboard = action.payload; })
      .addCase(fetchBgvDashboard.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Cases
      .addCase(fetchBgvCases.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBgvCases.fulfilled, (state, action) => { state.loading = false; state.cases = action.payload; })
      .addCase(fetchBgvCases.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Case Detail
      .addCase(fetchBgvCaseById.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBgvCaseById.fulfilled, (state, action) => { state.loading = false; state.currentCase = action.payload; })
      .addCase(fetchBgvCaseById.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Create Case
      .addCase(createBgvCase.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createBgvCase.fulfilled, (state) => { state.loading = false; })
      .addCase(createBgvCase.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Vendors
      .addCase(fetchBgvVendors.pending, (state) => { state.loading = true; })
      .addCase(fetchBgvVendors.fulfilled, (state, action) => { state.loading = false; state.vendors = action.payload; })
      .addCase(fetchBgvVendors.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Verification Types
      .addCase(fetchBgvVerificationTypes.pending, (state) => { state.loading = true; })
      .addCase(fetchBgvVerificationTypes.fulfilled, (state, action) => { state.loading = false; state.verificationTypes = action.payload; })
      .addCase(fetchBgvVerificationTypes.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { clearCurrentCase, clearError } = bgvSlice.actions;
export default bgvSlice.reducer;
